import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  Text,
  UnstyledButton,
  Group,
  Button,
  Box,
} from "@mantine/core";
import { IconTag, IconX } from "@tabler/icons";
import { OverflownText } from "../OverflownText";

interface CustomerMenuProps {
  customers: string[];
  onClose?: () => void;
  colTitle: string;
  showAddBtn?: boolean;
}

const CustomerMenu: React.FC<CustomerMenuProps> = ({
  customers,
  onClose,
  colTitle,
  showAddBtn = false,

}) => {
  const [visibleCustomers, setVisibleCustomers] = useState<string[]>([]);
  const [overflowCount, setOverflowCount] = useState<number>(0);
  const targetRef = useRef<HTMLButtonElement>(null);
  const [menuOpened, setMenuOpened] = useState(false);

  useEffect(() => {
    const updateVisibleCustomers = () => {
      if (!targetRef.current) return;

      const containerWidth = targetRef.current.offsetWidth;
      let totalWidth = 0;
      const visible: string[] = [];
      let overflow = 0;

      customers.forEach((cust) => {
        if (cust) {
          const customerWidth = Math.min(cust.length * 8 + 24, 100);
          if (totalWidth + customerWidth <= containerWidth - 30) {
            visible.push(cust);
            totalWidth += customerWidth;
          } else {
            overflow += 1;
          }
        }
      });

      setVisibleCustomers(visible);
      setOverflowCount(overflow);
    };

    updateVisibleCustomers();
    window.addEventListener("resize", updateVisibleCustomers);
    return () => window.removeEventListener("resize", updateVisibleCustomers);
  }, [customers]);

  return (
    <Menu
      position="bottom-start"
      shadow="sm"
      opened={menuOpened}
      onChange={setMenuOpened}
    >
      <Menu.Target>
        <UnstyledButton
          style={{ 
            width: "100%", 
            textAlign: "left",
            cursor: customers.length === 0 ? "default" : "pointer"
          }}
          ref={targetRef}
          onClick={() => customers.length === 0 ? null : setMenuOpened((o) => !o)}
        >
          <Group
            noWrap
            spacing={6}
            sx={{
              "&:hover": {
                backgroundColor: customers.length > 0 ? "#F8F8FB" : "transparent",
              },
              width: "min-content",
              textAlign: "left",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            <Group
              spacing={6}
              noWrap
              style={{
                flexWrap: "nowrap",
                overflow: "hidden",
                width: "min-content",
              }}
            >
              {visibleCustomers.map((customer) => (
                <OverflownText
                  key={customer}
                  size="sm"
                  style={{
                    backgroundColor: "rgb(125 88 243 / 10%)",
                    color: "rgb(125 88 243)",
                    padding: "0px 8px",
                    borderRadius: "4px",
                    display: "inline-block",
                    maxWidth: 100,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    border: "1px solid transparent"
                  }}
                  // title={customer.label}
                >
                  {customer}
                </OverflownText>
              ))}
              {overflowCount > 0 && (
                <Text
                  size="sm"
                  style={{
                    color: "#5C5CEB",
                    marginLeft: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{overflowCount}
                </Text>
              )}
              {showAddBtn && (
                <UnstyledButton
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    padding: "0px 4px",
                    borderRadius: 4,
                    color: "#5B60E3",
                    fontWeight: 500,
                    width: 82,
                  }}
                  onClick={() => setMenuOpened((o) => !o)}
                >
                  <IconTag size={12} strokeWidth={2} color="#5B60E3" />
                </UnstyledButton>
              )}
              {customers.length === 0 && (
                <Text
                  w="max-content"
                  size="sm"
                  color="#aeaeae"
                  lineClamp={1}
                  px={3}
                  sx={{
                    border:"1px solid transparent",
                    borderRadius: 4
                  }}
                >
                    No {colTitle.toLowerCase()}
                </Text>
              )}
            </Group>
          </Group>
        </UnstyledButton>
      </Menu.Target>
      {customers.length > 0 && (

        <Menu.Dropdown miw={200} p={0}>
          {customers.map((customer: string, index: number) => (
            <Menu.Item
              key={customer}
              w={"-webkit-fill-available"}
              p={4}
              m={4}
              mb={index !== customers.length - 1 ? 4 : 0}
              sx={{cursor:"default"}}
              closeMenuOnClick={false}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text
                  size="sm"
                  style={{
                    backgroundColor:"rgb(125 88 243 / 10%)",
                    color: "rgb(125 88 243)",
                    padding: "0px 4px",
                    borderRadius: "4px",
                    display: "inline-block",
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={customer}
                >
                  {customer}
                </Text>
              </div>
            </Menu.Item>
          ))}
          <Box
            sx={{
              borderRadius: "0 0 4px 4px",
              textAlignLast: "right",
              background: "#F8F9FA",
            }}
            w={"-webkit-fill-available"}
            mt={4}
            p={8}
          >
            <Button
              onClick={() => {
                setMenuOpened(false);
                onClose?.();
              }}
              styles={(theme) => ({
                root: {
                  backgroundColor: "#5B60E3",
                  border: 0,
                  height: 32,
                  fontSize: 12,
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#5B60E3", 0.05),
                  },
                },
                label: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              })}
              leftIcon={<IconX size={15} />}
            >
              Close
            </Button>
          </Box>
        </Menu.Dropdown>
      )}

    </Menu>
  );
};
export default CustomerMenu;

